import { Slot, SlotFillProvider, withFilters }              from '@wordpress/components';
import { __ }                                               from '@wordpress/i18n';
import { Formik, Form }                                     from 'formik';
import React                                                from 'react';
import cfwConvertOptionsObjectToArray                       from '../../../functions/cfwConvertOptionsObjectToArray';
import SettingsFormContainerPropsInterface                  from '../../../interfaces/SettingsFormContainerPropsInterface';
import SettingsFormPropsPlanInterface                       from '../../../interfaces/SettingsFormPropsPlanInterface';
import AdminPageSection                                     from '../AdminPageSection';
import CheckboxGroupField                                   from '../Fields/CheckboxGroupField';
import NumberField                                          from '../Fields/NumberField';
import TextField                                            from '../Fields/TextField';
import SecondaryButton                                      from '../SecondaryButton';
import ToggleCheckboxField                                  from '../Fields/ToggleCheckboxField';
import LockedFieldWrapper                                   from '../LockedFieldWrapper';

interface ACRSettingsInterface {
    enable_acr: boolean;
    acr_abandoned_time: number;
    acr_from_name: string;
    acr_from_address: string;
    acr_reply_to_address: string;
    acr_recovered_order_statuses: any[];
    acr_excluded_roles: any[];
    acr_simulate_only: boolean;
}

interface ACRFormWooCommerceSettingsInterface {
    order_statuses: any;
    roles: any;
}

interface ACRFormParamsInterface {
    pre_content: string;
    post_content: string;
    clear_cart_data_url: string;
}

interface ACRSettingsFormPropsInterface extends SettingsFormContainerPropsInterface {
    settings: ACRSettingsInterface;
    woocommerce_settings: ACRFormWooCommerceSettingsInterface;
    plan: SettingsFormPropsPlanInterface;
    params: ACRFormParamsInterface;
}

const ACRSettingsForm: React.FC<ACRSettingsFormPropsInterface> = ( props ) => {
    const { saveSettings, isLoading, searchTerm } = props;

    const AdditionalSettings = withFilters(
        'CheckoutWC.Admin.Settings.Pages.ACR',
    )( () => <></> );

    return (
        <SlotFillProvider>
            <AdditionalSettings />
            <Formik
                initialValues={{
                    ...props.settings,
                }}
                enableReinitialize={true}
                onSubmit={async ( values ) => {
                    await saveSettings( values );
                }}
            >
                {( { values } ) => (
                    <Form className={'space-y-6 transition-all'} style={{ filter: isLoading ? 'blur(2px)' : 'none' }}>
                        <AdminPageSection
                            title={__( 'Abandoned Cart Recovery', 'checkout-wc' )}
                            description={__( 'Configure Abandoned Cart Recovery settings.', 'checkout-wc' )}
                            content={
                                <>
                                    <LockedFieldWrapper plan={props.plan} slug={'order-review-step-one-page-checkout'} locked={props.plan.plan_level < 2} requiredPlans={props.plan.labels.required_list[ 2 ]}>
                                        <ToggleCheckboxField
                                            name="enable_acr"
                                            label={__( 'Enable Abandoned Cart Tracking', 'checkout-wc' )}
                                            description={__( 'Enable Abandoned Cart Recovery feature.', 'checkout-wc' )}
                                            disabled={props.plan.plan_level < 2}
                                            searchTerm={searchTerm}
                                        />

                                        {props.plan.plan_level >= 2 && values.enable_acr
                                            && (
                                                <NumberField
                                                    name='acr_abandoned_time'
                                                    label={__( 'Cart Is Abandoned After X Minutes', 'checkout-wc' )}
                                                    description={__( 'The number of minutes after which a cart is considered abandoned.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />
                                            )
                                        }
                                    </LockedFieldWrapper>

                                    <Slot name="CheckoutWC.Admin.Pages.ACR.Options" />
                                </>
                            }
                            post_content={props.params.post_content}
                        />

                        {props.plan.plan_level >= 2 && values.enable_acr
                            && (
                                <>
                                    <AdminPageSection
                                        title={__( 'Email Sending', 'checkout-wc' )}
                                        description={__( 'Configure email sending options.', 'checkout-wc' )}
                                        content={
                                            <>
                                                <ToggleCheckboxField
                                                    name="acr_simulate_only"
                                                    label={__( 'Disable Email Sending', 'checkout-wc' )}
                                                    description={__( 'Do not actually send any emails but allow carts to be tracked even if there are no emails configured.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />

                                                <TextField
                                                    name='acr_from_name'
                                                    label={__( 'From Name', 'checkout-wc' )}
                                                    description={__( 'The name you wish Abandoned Cart Recovery emails to be sent from.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />
                                                <TextField
                                                    name='acr_from_address'
                                                    label={__( 'From Address', 'checkout-wc' )}
                                                    description={__( 'The email address you wish Abandoned Cart Recovery emails to be sent from.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />
                                                <TextField
                                                    name='acr_reply_to_address'
                                                    label={__( 'Reply-To Address', 'checkout-wc' )}
                                                    description={__( 'The email address you wish Abandoned Cart Recovery emails replies to be sent to.', 'checkout-wc' )}
                                                    searchTerm={searchTerm}
                                                />

                                                <Slot name="CheckoutWC.Admin.Pages.ACR.EmailSending" />
                                            </>
                                        }
                                    />

                                    <AdminPageSection
                                        title={__( 'Advanced Options', 'checkout-wc' )}
                                        description={__( 'Configure advanced options.', 'checkout-wc' )}
                                        content={
                                            <>
                                                <CheckboxGroupField
                                                    name="acr_recovered_order_statuses"
                                                    label={__( 'Cart Recovered Order Statuses', 'checkout-wc' )}
                                                    description={__( 'Choose which Order Statuses indicate a successful order.', 'checkout-wc' )}
                                                    options={cfwConvertOptionsObjectToArray( props.woocommerce_settings.order_statuses )}
                                                    searchTerm={searchTerm}
                                                />
                                                <CheckboxGroupField
                                                    name="acr_excluded_roles"
                                                    label={__( 'Exclude From Abandoned Cart Recovery By Role', 'checkout-wc' )}
                                                    description={__( 'Check any user role that should be excluded from abandoned cart emails.', 'checkout-wc' )}
                                                    options={cfwConvertOptionsObjectToArray( props.woocommerce_settings.roles )}
                                                    searchTerm={searchTerm}
                                                />

                                                <Slot name="CheckoutWC.Admin.Pages.ACR.AdvancedOptions" />
                                            </>
                                        }
                                    />

                                    <AdminPageSection
                                        title={__( 'Danger Zone', 'checkout-wc' )}
                                        description={__( 'Clear your cart data.', 'checkout-wc' )}
                                        content={
                                            <>
                                                <SecondaryButton
                                                    url={props.params.clear_cart_data_url}
                                                    buttonText={__( 'Delete All Tracked Carts', 'checkout-wc' )}
                                                    onClick={( e ) => {
                                                    // eslint-disable-next-line no-alert
                                                        if ( !window.confirm( __( 'Are you sure you want to delete all tracked carts?', 'checkout-wc' ) ) ) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />

                                                <p>{__( 'Note: This resets ALL abandoned cart recovery statistics!', 'checkout-wc' )}</p>

                                                <Slot name="CheckoutWC.Admin.Pages.ACR.DangerZone" />
                                            </>
                                        }
                                    />
                                </>
                            )
                        }

                        <button className={'cfw_admin_page_submit hidden'} type="submit">Submit</button>
                    </Form>
                )}
            </Formik>
        </SlotFillProvider>
    );
};

export default ACRSettingsForm;
